import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { FormikContext } from 'formik';

import { UseFieldErrorHandlerInput, UseFieldErrorHandlerOutput } from './types';
import {
    errorsToString,
    getErrorsByType,
    getNestedProperty,
    mergeErrorArrays,
    normalizeErrorMessage,
    processErrorStateChange,
    removeErrorsFromFormString,
    validateFieldName,
} from './utils';
import { DEFAULT_CONFIG, DEFAULT_ERROR_MESSAGE_SEPARATOR, ERROR_TYPES } from './utils/constants';

/**
 * Enhanced field error handler hook that integrates with form libraries
 *
 * This hook provides a complete solution for managing field errors across
 * different sources: form validation, input component validation, and prop-provided errors.
 *
 * Key features:
 * - Form library agnostic (with adapters)
 * - Automatic error synchronization
 * - Deduplication and merging of errors
 * - Performance optimized with minimal re-renders
 *
 * @param input - Hook configuration and parameters
 * @returns Enhanced error handling interface
 */
export function useFieldErrorHandler(input: UseFieldErrorHandlerInput): UseFieldErrorHandlerOutput {
    const { fieldName, errorMessage, formAdapter: providedFormAdapter, config = {} } = input;

    // Validate input
    validateFieldName(fieldName);

    // Merge config with defaults
    const finalConfig = { ...DEFAULT_CONFIG, ...config };
    const separator = finalConfig.errorMessageSeparator || DEFAULT_ERROR_MESSAGE_SEPARATOR;

    // Safely get Formik context using useContext directly to avoid warnings
    const formikContext = useContext(FormikContext);

    // Create Formik adapter if context is available
    const formikAdapter = useMemo(() => {
        if (!formikContext) {
            return null;
        }

        const { setFieldError, errors, isValidating } = formikContext;

        return {
            getFieldError: (fieldName: string) => {
                const error = getNestedProperty(errors, fieldName) as string | undefined;
                return error ?? undefined;
            },
            setFieldError: (fieldName: string, error: string | string[] | undefined) => {
                const errorString = Array.isArray(error)
                    ? errorsToString(error, DEFAULT_ERROR_MESSAGE_SEPARATOR)
                    : error;
                setFieldError(fieldName, errorString);
            },
            isValidating: () => {
                return isValidating;
            },
            getFormContext: () => {
                return formikContext;
            },
        };
    }, [formikContext]);

    // Create or use provided form adapter
    const formAdapter = useMemo(() => {
        if (providedFormAdapter) {
            return providedFormAdapter;
        }

        if (formikAdapter) {
            return formikAdapter;
        }

        // No form context available, create a minimal adapter
        return {
            getFieldError: () => {
                return undefined;
            },
            setFieldError: () => {
                // No-op when no form context
            },
            isValidating: () => {
                return false;
            },
        };
    }, [providedFormAdapter, formikAdapter]);

    // Internal state
    const [inputErrors, setInputErrorsInternal] = useState<string>('');
    const [propErrorsCleared, setPropErrorsCleared] = useState<boolean>(false);

    // Normalize prop errors
    const propErrors = useMemo(() => {
        const normalized = normalizeErrorMessage(errorMessage, separator);
        return propErrorsCleared ? [] : normalized;
    }, [errorMessage, separator, propErrorsCleared]);

    const setInputErrors = useCallback(
        (errorOrUpdater: string | ((prev: string) => string)) => {
            const newError =
                typeof errorOrUpdater === 'function' ? errorOrUpdater(inputErrors) : errorOrUpdater;

            setInputErrorsInternal(newError);

            // Immediate sync if auto-sync is enabled
            if (finalConfig.autoSyncToForm) {
                const normalizedNewErrors = normalizeErrorMessage(newError, separator);
                const currentFormErrors = normalizeErrorMessage(
                    formAdapter.getFieldError(fieldName),
                    separator
                );
                const allErrors = mergeErrorArrays([
                    currentFormErrors,
                    normalizedNewErrors,
                    propErrors,
                ]);
                const errorString = errorsToString(allErrors, separator);

                formAdapter.setFieldError(fieldName, errorString || undefined);
            }
        },
        [inputErrors, finalConfig.autoSyncToForm, separator, formAdapter, fieldName, propErrors]
    );

    // Get current form error
    const currentFormError = useMemo(() => {
        const error = formAdapter.getFieldError(fieldName);
        return error;
    }, [formAdapter, fieldName]);

    // Create current error state map including the latest form error
    const currentErrorStateMap = useMemo(() => {
        return {
            [ERROR_TYPES.FORM_ERROR]: typeof currentFormError === 'string' ? currentFormError : '',
            [ERROR_TYPES.INPUT_ERROR]: inputErrors,
            [ERROR_TYPES.PROP_ERROR]: errorsToString(propErrors, separator),
        };
    }, [currentFormError, inputErrors, propErrors, separator]);

    // Process error state and determine updates
    const errorProcessingResult = useMemo(() => {
        return processErrorStateChange(currentErrorStateMap, inputErrors, propErrors, separator);
    }, [currentErrorStateMap, inputErrors, propErrors, separator]);

    const { finalErrors, newFormError, shouldUpdateFormError } = errorProcessingResult;

    // Sync with form when auto-sync is enabled
    useEffect(() => {
        if (finalConfig.autoSyncToForm && shouldUpdateFormError && newFormError !== undefined) {
            formAdapter.setFieldError(fieldName, newFormError);
        }
    }, [finalConfig.autoSyncToForm, shouldUpdateFormError, newFormError, formAdapter, fieldName]);

    // Memoize normalized errors and merged error arrays to reduce effect dependencies
    const memoizedCurrentFormErrors = useMemo(() => {
        return normalizeErrorMessage(currentFormError, separator);
    }, [currentFormError, separator]);

    const memoizedCurrentInputErrors = useMemo(() => {
        return normalizeErrorMessage(inputErrors, separator);
    }, [inputErrors, separator]);

    const memoizedNewInputErrors = useMemo(() => {
        return memoizedCurrentInputErrors.filter((error) => {
            return !memoizedCurrentFormErrors.includes(error);
        });
    }, [memoizedCurrentInputErrors, memoizedCurrentFormErrors]);

    const memoizedAllErrorsToSync = useMemo(() => {
        return mergeErrorArrays([
            memoizedCurrentFormErrors,
            memoizedCurrentInputErrors,
            propErrors,
        ]);
    }, [memoizedCurrentFormErrors, memoizedCurrentInputErrors, propErrors]);

    const memoizedErrorStringToSync = useMemo(() => {
        return errorsToString(memoizedAllErrorsToSync, separator);
    }, [memoizedAllErrorsToSync, separator]);

    // Additional auto-sync for immediate input error changes (now redundant since setInputErrors handles this)
    // This effect is kept for backward compatibility but won't trigger as often
    useEffect(() => {
        if (finalConfig.autoSyncToForm && inputErrors) {
            // Only sync if there are new input errors
            if (memoizedNewInputErrors.length > 0) {
                formAdapter.setFieldError(fieldName, memoizedErrorStringToSync || undefined);
            }
        }
    }, [
        finalConfig.autoSyncToForm,
        inputErrors,
        memoizedNewInputErrors,
        memoizedErrorStringToSync,
        formAdapter,
        fieldName,
    ]);

    // Cleanup resolved errors from form
    useEffect(() => {
        if (!finalConfig.autoCleanup || !currentFormError) {
            return;
        }

        const formErrors = normalizeErrorMessage(currentFormError, separator);
        const currentInputErrorsList = normalizeErrorMessage(inputErrors, separator);

        // Find errors that are no longer present in input or prop errors
        const errorsToRemove = formErrors.filter((error) => {
            return !currentInputErrorsList.includes(error) && !propErrors.includes(error);
        });

        if (errorsToRemove.length > 0) {
            const updatedFormError = removeErrorsFromFormString(
                typeof currentFormError === 'string' ? currentFormError : '',
                errorsToRemove,
                separator
            );
            formAdapter.setFieldError(fieldName, updatedFormError);
        }
    }, [
        finalConfig.autoCleanup,
        currentFormError,
        inputErrors,
        propErrors,
        separator,
        formAdapter,
        fieldName,
    ]);

    // Utility functions
    const clearErrors = useCallback(() => {
        setInputErrors('');
        setPropErrorsCleared(true);
    }, [setInputErrors]);

    const addError = useCallback(
        (error: string) => {
            if (!error || error.trim().length === 0) {
                return;
            }

            setInputErrors((prev) => {
                const currentErrors = normalizeErrorMessage(prev, separator);
                if (currentErrors.includes(error)) {
                    return prev; // Error already exists
                }
                return errorsToString([...currentErrors, error], separator);
            });
        },
        [setInputErrors, separator]
    );

    const removeError = useCallback(
        (error: string) => {
            if (!error) {
                return;
            }

            setInputErrors((prev) => {
                const currentErrors = normalizeErrorMessage(prev, separator);
                const filteredErrors = currentErrors.filter((existingError) => {
                    return existingError !== error;
                });
                return errorsToString(filteredErrors, separator);
            });
        },
        [setInputErrors, separator]
    );

    const getErrorsByTypeCallback = useCallback(
        (errorType: ERROR_TYPES) => {
            return getErrorsByType(currentErrorStateMap, errorType, separator);
        },
        [currentErrorStateMap, separator]
    );

    const syncWithForm = useCallback(() => {
        const allErrors = errorsToString(finalErrors, separator);
        formAdapter.setFieldError(fieldName, allErrors || undefined);
    }, [finalErrors, separator, formAdapter, fieldName]);

    // Calculate has error state
    const hasError = useMemo(() => {
        return finalErrors.length > 0;
    }, [finalErrors]);

    // Always return the full interface (remove the early return optimization)
    return {
        inputErrors,
        setInputErrors,
        finalErrors,
        hasError,
        clearErrors,
        addError,
        removeError,
        getErrorsByType: getErrorsByTypeCallback,
        syncWithForm,
    };
}

export default useFieldErrorHandler;
